import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import Const from 'constants/constant'
import Quill from 'quill'
import CustomBlotFormatter from 'quill/CustomBlotFormatter'
import registerQuillImageHandler from 'utils/quillUtils'

import 'quill/dist/quill.snow.css'

// ---------------------------------------------------------------------------
// Module-level registration: runs once when this file is first imported,
// guaranteeing that blotFormatter and MentionBlot are in the Quill registry
// BEFORE useQuill() creates any Quill instance (useQuill's internal useEffect
// runs before any useEffect defined later in the component, so per-instance
// registration inside useEffect is always too late on the very first mount).
// ---------------------------------------------------------------------------
if (!Quill.imports['modules/blotFormatter']) {
  Quill.register('modules/blotFormatter', CustomBlotFormatter)
}

if (!Quill.imports['formats/mention']) {
  const Embed = Quill.import('blots/embed') as any

  class MentionBlot extends Embed {
    static blotName = 'mention'

    static tagName = 'span'

    // Narrow the match to only our mention spans.
    // Without this, Quill's generic span matcher would also match the
    // internal <span class="ql-ui"> bullet elements in list items,
    // causing a spurious '@' to appear.
    static className = 'ql-mention'

    static create(value: any) {
      const node = super.create(value) as HTMLElement
      let type = 'user'
      let targetId = ''
      let name = ''

      if (typeof value === 'object' && value !== null) {
        type = value.type || 'user'
        targetId = value.targetId ? String(value.targetId) : ''
        name = value.name || ''
      } else if (typeof value === 'string') {
        name = value.replace(/^@/, '')
      }

      node.setAttribute('contenteditable', 'false')
      node.setAttribute('data-mention-type', type)
      if (targetId) {
        node.setAttribute('data-target-id', targetId)
      }

      let colorClass = 'text-sky-600'
      if (type === 'all') {
        colorClass = 'text-amber-600'
      }
      if (type === 'group') {
        colorClass = 'text-purple-700'
      }

      node.className = `ql-mention ${colorClass} font-semibold select-none`
      node.textContent = name
      return node
    }

    static value(node: HTMLElement) {
      return {
        type: node.getAttribute('data-mention-type'),
        targetId: node.getAttribute('data-target-id'),
        name: node.textContent?.replace(/^@/, '') || ''
      }
    }
  }

  Quill.register('formats/mention', MentionBlot)
}

if (!(Quill as any).isGetFormatPatched) {
  const originalGetFormat = Quill.prototype.getFormat
  Quill.prototype.getFormat = function getFormat(
    this: any,
    index?: any,
    length: number = 0
  ) {
    if (index == null) {
      const sel =
        typeof this.getSelection === 'function' ? this.getSelection(true) : null
      if (sel == null) return {}
      index = sel
    }
    if (typeof index === 'number') {
      return this.editor ? this.editor.getFormat(index, length) : {}
    }
    if (index && typeof index.index === 'number') {
      return this.editor
        ? this.editor.getFormat(index.index, index.length || 0)
        : {}
    }
    return originalGetFormat ? originalGetFormat.call(this, index) : {}
  }
  ;(Quill as any).isGetFormatPatched = true
}

export interface QuillEditorV3Ref {
  insertMention: (
    type: string,
    name: string,
    targetId?: number | string
  ) => void
  getQuill: () => any
  focusEnd: () => void
  setContent: (content: string) => void
  clear: () => void
}

export interface QuillEditorV3Props {
  value?: string
  onChange?: (content: string) => void
  onMentionTrigger?: (pos?: {
    left: number
    top: number
    query?: string
  }) => void
  onMentionClose?: () => void
  onKeyDown?: (e: KeyboardEvent) => boolean | void
  readOnly?: boolean
  placeholder?: string
  className?: string
  height?: number | string
}

/**
 * Check if the HTML content from Quill Editor is truly empty.
 * Handles cases where Quill generates empty HTML tags.
 */
export const isQuillEmpty = (htmlContent?: string) => {
  if (!htmlContent) return true

  if (
    htmlContent === '<p><br></p>' ||
    htmlContent === '<p></p>' ||
    htmlContent === '<br>'
  ) {
    return true
  }

  const hasMedia =
    htmlContent.includes('<img') ||
    htmlContent.includes('<iframe') ||
    htmlContent.includes('<video')

  if (hasMedia) return false

  const strippedContent = htmlContent
    .replace(/<[^>]*>?/gm, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return strippedContent.length === 0
}

/**
 * QuillEditorV3: Rich text editor supporting images, formatting,
 * cursor bounds tracking for mention dropdowns, and HTML badge insertion.
 */
const QuillEditorV3 = forwardRef<QuillEditorV3Ref, QuillEditorV3Props>(
  (
    {
      value,
      onChange,
      onMentionTrigger,
      onMentionClose,
      onKeyDown,
      readOnly = false,
      placeholder = '入力してください',
      className,
      height = 250
    },
    ref
  ) => {
    const editorContainerRef = useRef<HTMLDivElement>(null)
    const [quill, setQuill] = useState<Quill | undefined>(undefined)
    const lastHtmlRef = useRef<string>('__quill_init__')
    const emittedValuesRef = useRef<Set<string>>(new Set())
    const lastSelectionRef = useRef<{ index: number; length: number } | null>(
      null
    )

    const { t } = useTranslation()

    useEffect(() => {
      const container = editorContainerRef.current
      if (!container) return undefined

      container.innerHTML = ''

      const editorHolder = document.createElement('div')
      container.appendChild(editorHolder)

      const instance = new Quill(editorHolder, {
        theme: 'snow',
        modules: Const.QUILL_MODULES,
        formats: [...Const.QUILL_FORMATS, 'mention'],
        placeholder: isQuillEmpty(value) ? placeholder : ''
      })

      if (value && !isQuillEmpty(value)) {
        if (instance.clipboard?.dangerouslyPasteHTML) {
          instance.clipboard.dangerouslyPasteHTML(value)
        } else {
          instance.root.innerHTML = value
        }
        lastHtmlRef.current = instance.root.innerHTML
      }

      setQuill(instance)

      return () => {
        setQuill(undefined)
        container.innerHTML = ''
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useImperativeHandle(
      ref,
      () => ({
        insertMention: (
          type: string,
          name: string,
          targetId?: number | string
        ) => {
          if (!quill) return
          const sel = quill.getSelection() || { index: quill.getLength() }
          const currentIndex = sel.index
          const contents = quill.getContents(0, currentIndex)
          let atIndex = -1

          if (contents && contents.ops && contents.ops.length > 0) {
            let runningIndex = 0
            for (let i = 0; i < contents.ops.length; i += 1) {
              const op = contents.ops[i]
              if (typeof op.insert === 'string') {
                const str = op.insert
                if (i === contents.ops.length - 1) {
                  const lastAt = str.lastIndexOf('@')
                  if (lastAt !== -1) {
                    atIndex = runningIndex + lastAt
                  }
                }
                runningIndex += str.length
              } else {
                runningIndex += 1
              }
            }
          }

          if (atIndex !== -1) {
            quill.deleteText(atIndex, currentIndex - atIndex)
            quill.insertEmbed(atIndex, 'mention', { type, name, targetId })
            quill.insertText(atIndex + 1, ' ', 'user')
            quill.setSelection(atIndex + 2, 0, 'user')
          } else {
            quill.insertEmbed(currentIndex, 'mention', { type, name, targetId })
            quill.insertText(currentIndex + 1, ' ', 'user')
            quill.setSelection(currentIndex + 2, 0, 'user')
          }
        },
        focusEnd: () => {
          if (!quill) return
          quill.focus()
          const length = quill.getLength()
          quill.setSelection(length, 0, 'user')
        },
        setContent: (html: string) => {
          if (!quill) return
          emittedValuesRef.current.clear()
          if (isQuillEmpty(html)) {
            quill.setText('')
          } else if (quill.clipboard?.dangerouslyPasteHTML) {
            quill.clipboard.dangerouslyPasteHTML(html)
          } else {
            quill.root.innerHTML = html
          }
          lastHtmlRef.current = quill.root.innerHTML
        },
        clear: () => {
          if (!quill) return
          emittedValuesRef.current.clear()
          quill.setText('')
          lastHtmlRef.current = quill.root.innerHTML
        },
        getQuill: () => quill
      }),
      [quill]
    )

    useEffect(() => {
      registerQuillImageHandler(quill, t)
    }, [quill, t])

    useEffect(() => {
      if (quill) {
        if (!(quill as any).isSelectionPatched) {
          const originalGetSelection = quill.getSelection.bind(quill)
          ;(quill as any).getSelection = (focus = false): any => {
            const range = originalGetSelection(focus)
            if (range) {
              lastSelectionRef.current = range
              return range
            }
            if (focus) {
              return (
                lastSelectionRef.current || {
                  index: Math.max(
                    0,
                    (quill.getLength ? quill.getLength() : 1) - 1
                  ),
                  length: 0
                }
              )
            }
            return null
          }
          ;(quill as any).isSelectionPatched = true
        }

        const toolbar = editorContainerRef.current?.querySelector(
          '.ql-toolbar'
        ) as HTMLElement | null
        const handleToolbarMouseDown = (e: MouseEvent) => {
          const target = e.target as HTMLElement
          if (
            target &&
            target.tagName !== 'SELECT' &&
            target.tagName !== 'INPUT' &&
            target.tagName !== 'OPTION'
          ) {
            e.preventDefault()
          }
        }
        if (toolbar) {
          toolbar.addEventListener(
            'mousedown',
            handleToolbarMouseDown as EventListener
          )
        }

        const { tooltip } = (quill as any).theme
        if (tooltip && !tooltip.isPatched) {
          const originalPosition = tooltip.position
          if (typeof originalPosition === 'function') {
            tooltip.position = function positionTooltip(reference: any) {
              const shift = originalPosition.call(tooltip, reference)
              if (tooltip.root && quill.container) {
                const containerRect = quill.container.getBoundingClientRect()
                const tooltipRect = tooltip.root.getBoundingClientRect()
                const windowHeight = window.innerHeight

                if (
                  tooltipRect.bottom > containerRect.bottom + 10 ||
                  tooltipRect.bottom > windowHeight - 50
                ) {
                  const currentTop = parseFloat(tooltip.root.style.top || '0')
                  const refHeight = reference
                    ? reference.bottom - reference.top
                    : 20
                  const newTop = Math.max(
                    -10,
                    currentTop - tooltipRect.height - refHeight - 14
                  )
                  tooltip.root.style.top = `${newTop}px`
                }
              }
              return shift
            }
          }

          const originalEdit = tooltip.edit
          tooltip.edit = (mode: string, value: string) => {
            originalEdit.call(tooltip, mode, value)
            if (mode === 'link') {
              const input = tooltip.root.querySelector('input[data-link]')
              if (input) {
                input.setAttribute('placeholder', 'https://')
              }
            }
          }
          tooltip.isPatched = true
        }
        quill.enable(!readOnly)

        return () => {
          if (toolbar) {
            toolbar.removeEventListener(
              'mousedown',
              handleToolbarMouseDown as EventListener
            )
          }
        }
      }
      return undefined
    }, [quill, readOnly])

    useEffect(() => {
      if (quill) {
        const isEmpty = isQuillEmpty(value)
        const actualPlaceholder = isEmpty ? placeholder : ''
        quill.root.setAttribute('data-placeholder', actualPlaceholder)

        if (!isEmpty) {
          quill.root.classList.remove('ql-blank')
        } else {
          quill.root.classList.add('ql-blank')
        }
      }
    }, [quill, value, placeholder])

    useEffect(() => {
      if (!quill || value === undefined) return

      const isIncomingEmpty = isQuillEmpty(value)
      const isCurrentEditorEmpty = isQuillEmpty(quill.root.innerHTML)

      // If both incoming and editor are empty, sync lastHtmlRef and don't touch DOM
      if (isIncomingEmpty && isCurrentEditorEmpty) {
        lastHtmlRef.current = quill.root.innerHTML
        return
      }

      // If incoming value is empty, clear the editor and clear emitted values cache
      if (isIncomingEmpty) {
        if (!isCurrentEditorEmpty) {
          quill.setText('')
        }
        emittedValuesRef.current.clear()
        lastHtmlRef.current = quill.root.innerHTML
        return
      }

      // Ignore updates that are echoing our own emitted HTML only if current editor is not empty
      if (
        value === lastHtmlRef.current ||
        (!isCurrentEditorEmpty && emittedValuesRef.current.has(value))
      ) {
        return
      }

      if (value !== quill.root.innerHTML) {
        if (quill.clipboard?.dangerouslyPasteHTML) {
          quill.clipboard.dangerouslyPasteHTML(value)
        } else {
          quill.root.innerHTML = value
        }
        lastHtmlRef.current = quill.root.innerHTML
      }
    }, [quill, value])

    const onChangeRef = useRef(onChange)
    const onMentionTriggerRef = useRef(onMentionTrigger)
    const onMentionCloseRef = useRef(onMentionClose)
    const onKeyDownRef = useRef(onKeyDown)

    useEffect(() => {
      onChangeRef.current = onChange
      onMentionTriggerRef.current = onMentionTrigger
      onMentionCloseRef.current = onMentionClose
      onKeyDownRef.current = onKeyDown
    }, [onChange, onMentionTrigger, onMentionClose, onKeyDown])

    useEffect(() => {
      if (!quill) return

      const notifyIfHtmlChanged = () => {
        const html = quill.root.innerHTML
        if (html !== lastHtmlRef.current) {
          lastHtmlRef.current = html
          emittedValuesRef.current.add(html)
          if (emittedValuesRef.current.size > 50) {
            const first = emittedValuesRef.current.values().next().value
            if (first !== undefined) {
              emittedValuesRef.current.delete(first)
            }
          }
          if (onChangeRef.current) {
            onChangeRef.current(html)
          }
        }
      }

      const checkMentionTrigger = () => {
        const range = quill.getSelection()
        if (!range) {
          // If range is null, the editor lost focus (e.g., user clicked the search box).
          // We intentionally do NOT close the menu here, otherwise the user can't type in the search box.
          return
        }

        const currentIndex = range.index
        const textBefore = quill.getText(0, currentIndex)
        const lastAt = textBefore.lastIndexOf('@')

        if (lastAt !== -1) {
          const textAfterAt = textBefore.slice(lastAt)
          if (!/\s/.test(textAfterAt)) {
            const queryText = textBefore.slice(lastAt + 1)
            const bounds =
              quill.getBounds(currentIndex) || quill.getBounds(lastAt)

            const toolbar = quill.root.parentElement
              ?.previousElementSibling as HTMLElement
            const toolbarHeight = toolbar ? toolbar.offsetHeight : 44

            if (onMentionTriggerRef.current) {
              onMentionTriggerRef.current({
                left: bounds ? bounds.left : 10,
                top: bounds ? bounds.top + toolbarHeight : 10,
                query: queryText
              })
            }
            return
          }
        }
        if (onMentionCloseRef.current) onMentionCloseRef.current()
      }

      const deferredCheckMentionTrigger = () => {
        setTimeout(checkMentionTrigger, 0)
      }

      const handleCompositionStart = () => {
        if (quill.root) {
          quill.root.classList.add('is-composing')
          quill.root.classList.remove('ql-blank')
        }
      }

      const handleCompositionEnd = () => {
        if (quill.root) {
          quill.root.classList.remove('is-composing')
          if (!isQuillEmpty(quill.root.innerHTML)) {
            quill.root.classList.remove('ql-blank')
          } else {
            quill.root.classList.add('ql-blank')
          }
        }
      }

      const handleInput = () => {
        if (quill.root && !isQuillEmpty(quill.root.innerHTML)) {
          quill.root.classList.remove('ql-blank')
        }
      }

      const handleKeyDown = (e: KeyboardEvent) => {
        if (onKeyDownRef.current) {
          const isHandled = onKeyDownRef.current(e)
          if (isHandled) {
            e.preventDefault()
            e.stopPropagation()
          }
        }
      }

      if (quill.root) {
        quill.root.addEventListener('compositionstart', handleCompositionStart)
        quill.root.addEventListener('compositionend', handleCompositionEnd)
        quill.root.addEventListener('input', handleInput)
        quill.root.addEventListener('keydown', handleKeyDown, true)
      }

      const handleEditorEvents = () => {
        notifyIfHtmlChanged()
        deferredCheckMentionTrigger()
      }

      const handleSelectionChange = (range: any) => {
        if (range) {
          lastSelectionRef.current = range
        }
        deferredCheckMentionTrigger()
      }

      quill.on('text-change', handleEditorEvents)
      quill.on('selection-change', handleSelectionChange)
      quill.on('editor-change', notifyIfHtmlChanged)

      return () => {
        if (quill.root) {
          quill.root.removeEventListener(
            'compositionstart',
            handleCompositionStart
          )
          quill.root.removeEventListener('compositionend', handleCompositionEnd)
          quill.root.removeEventListener('input', handleInput)
          quill.root.removeEventListener('keydown', handleKeyDown, true)
        }
        quill.off('text-change', handleEditorEvents)
        quill.off('selection-change', handleSelectionChange)
        quill.off('editor-change', notifyIfHtmlChanged)
      }
    }, [quill])

    const containerHeight =
      typeof height === 'number' ? `${height}px` : height || '180px'

    return (
      <div className={clsx('quill-custom-editor', className)}>
        <div
          ref={editorContainerRef}
          style={
            {
              '--quill-container-height': containerHeight
            } as React.CSSProperties
          }
          className="[&_.ql-container]:!h-[var(--quill-container-height)] [&_.ql-editor]:!min-h-full"
        />
      </div>
    )
  }
)

QuillEditorV3.defaultProps = {
  value: '',
  onChange: undefined,
  onMentionTrigger: undefined,
  onMentionClose: undefined,
  onKeyDown: undefined,
  readOnly: false,
  placeholder: '入力してください',
  className: '',
  height: 250
}

export default React.memo(QuillEditorV3)
